// iot-admin/client/src/pages/VehicleDetail.js
//
// One car — its particulars, its papers, and every screen it has carried.
//
// ── THE PAPERS ARE THE POINT ────────────────────────────────────────────────
// Before V250 a car was the device's name plus a photo of its number plate. A
// commercial cab needs RC, insurance, PUC, fitness and permit, and every one of
// those lapses. This screen is where they live, each with its own expiry, so
// "what needs renewing" stops being something someone has to remember.
//
// ── EDITING LIVES HERE, NOT ON THE DEVICE PAGE ──────────────────────────────
// The device page can only FIT a screen into a car. Editing what a car IS
// belongs on the car — same separation V249 established for drivers, and for
// the same reason: when one form did both, the destructive reading was the
// default.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { vehicleService } from '../services/api';
import { ImageField } from '../components/DeviceDocImage';
import '../components/DeviceSimTaxiDriver.css';

const FUEL_TYPES = ['petrol', 'diesel', 'cng', 'lpg', 'electric', 'hybrid'];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
/** yyyy-mm-dd for <input type="date">, which will not accept an ISO timestamp. */
const forInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const span = (from, to) => {
  if (!from) return '';
  const days = Math.max(0, Math.round(((to ? new Date(to) : new Date()) - new Date(from)) / 86400000));
  if (days === 0) return 'less than a day';
  if (days === 1) return '1 day';
  if (days < 60) return `${days} days`;
  const m = Math.round(days / 30);
  return m < 24 ? `${m} months` : `${Math.round(m / 12)} years`;
};

// Order matters: this is the order someone checks papers in.
const DOC_ORDER = ['rc', 'insurance', 'puc', 'fitness', 'permit'];

const STATE_STYLE = {
  ok:       { cls: 'status-active',   text: (d) => `Valid until ${fmtDate(d.expiresOn)}` },
  expiring: { cls: 'status-pending',  text: (d) => `Expires in ${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'}` },
  expired:  { cls: 'status-error',    text: (d) => `Expired ${Math.abs(d.daysLeft)} days ago` },
  undated:  { cls: 'status-pending',  text: () => 'On file · no expiry recorded' },
  missing:  { cls: 'status-disabled', text: () => 'Not on file' },
};

const VehicleDetail = () => {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState({});   // { docType: bool }

  const fetchVehicle = useCallback(async () => {
    try {
      setLoading(true);
      const res = await vehicleService.getById(vehicleId);
      setVehicle(res.data);
      setForm({
        registration: res.data.registration || '',
        make: res.data.make || '',
        model: res.data.model || '',
        modelYear: res.data.modelYear || '',
        colour: res.data.colour || '',
        fuelType: res.data.fuelType || '',
        notes: res.data.notes || '',
      });
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Vehicle not found' : 'Failed to load vehicle');
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { fetchVehicle(); }, [fetchVehicle]);

  const save = async () => {
    if (form.registration.trim().length < 4) { setError('Registration must be at least 4 characters'); return; }
    setSaving(true);
    setError(null);
    try {
      await vehicleService.update(vehicleId, {
        registration: form.registration.trim(),
        make: form.make.trim() || null,
        model: form.model.trim() || null,
        modelYear: form.modelYear ? Number(form.modelYear) : null,
        colour: form.colour.trim() || null,
        fuelType: form.fuelType || null,
        notes: form.notes.trim() || null,
      });
      setMessage('Vehicle updated');
      setEditing(false);
      await fetchVehicle();
    } catch (e) {
      // 409 = that registration is already another car's. The server refuses
      // rather than merging two vehicles.
      setError(e.response?.data?.error || 'Failed to update vehicle');
    } finally {
      setSaving(false);
    }
  };

  // Only offered for a car that has never carried a screen; the server enforces
  // it independently. Hiding the button means the refusal is not how someone
  // discovers the rule.
  const remove = async () => {
    if (!window.confirm(`Delete ${vehicle.registration}? This vehicle has never carried a screen, so nothing is lost.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await vehicleService.remove(vehicleId);
      navigate('/vehicles');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete vehicle');
      setDeleting(false);
    }
  };

  const uploadDoc = async (docType, file) => {
    setUploading((u) => ({ ...u, [docType]: true }));
    setError(null);
    try {
      const key = await vehicleService.uploadDocument(vehicleId, docType, file);
      await vehicleService.saveDocument(vehicleId, docType, key);
      setMessage('Document uploaded');
      await fetchVehicle();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [docType]: false }));
    }
  };

  // The expiry is saved on its own. `key` is left OUT of the payload rather
  // than sent as null — omitted means "leave the image alone", null means
  // "clear it", and recording a renewal date must not delete the certificate.
  const saveExpiry = async (docType, value) => {
    setError(null);
    try {
      await vehicleService.saveDocument(vehicleId, docType, undefined, value || null);
      await fetchVehicle();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save expiry');
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!vehicle) {
    return (
      <div className="users-page">
        <Link to="/vehicles" className="back-link">← Back to Vehicles</Link>
        <div className="alert alert-error">❌ {error || 'Vehicle not found'}</div>
      </div>
    );
  }

  const fitted = vehicle.fittings.filter((f) => f.isActive);
  const past = vehicle.fittings.filter((f) => !f.isActive);
  const needsAttention = DOC_ORDER
    .map((k) => vehicle.documents[k])
    .filter((d) => d && (d.state === 'expired' || d.state === 'expiring'));

  return (
    <div className="users-page">
      <Link to="/vehicles" className="back-link">← Back to Vehicles</Link>

      {message && <div className="alert alert-success">✅ {message}</div>}
      {error && <div className="alert alert-error">❌ {error}</div>}

      <div className="user-profile-card">
        <div className="profile-header">
          <div className="user-avatar large">🚗</div>
          <div className="profile-info">
            <h1>{vehicle.registration}</h1>
            <p className="email">
              {[vehicle.make, vehicle.model, vehicle.modelYear, vehicle.colour, vehicle.fuelType?.toUpperCase()]
                .filter(Boolean).join(' · ') || 'No particulars on file'}
            </p>
          </div>
        </div>
      </div>

      {/* The one thing worth interrupting for. */}
      {needsAttention.length > 0 && (
        <div className="alert alert-warning">
          ⚠️ {needsAttention.map((d) => (
            d.state === 'expired'
              ? `${d.label} expired ${fmtDate(d.expiresOn)}`
              : `${d.label} expires in ${d.daysLeft} day${d.daysLeft === 1 ? '' : 's'}`
          )).join(' · ')}
        </div>
      )}

      {/* ── Particulars ── */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Vehicle</h2>
          {!editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>✏️ Edit details</button>
              {vehicle.fittings.length === 0 && (
                <button className="btn btn-danger btn-sm" onClick={remove} disabled={deleting}>
                  {deleting ? 'Deleting…' : '🗑️ Delete'}
                </button>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <>
            {vehicle.fittings.length > 0 && (
              <div className="std-hint">
                This edits <strong>the vehicle</strong>. It has carried {vehicle.fittings.length} screen
                {vehicle.fittings.length === 1 ? '' : 's'} — the change shows wherever this car appears,
                including on the operator&rsquo;s console. To put a screen in a <em>different</em> car, use
                the device page instead.
              </div>
            )}
            <div className="detail-grid">
              <div className="detail-item">
                <label>Registration number</label>
                <input className="std-input" value={form.registration}
                  onChange={(e) => setForm((f) => ({ ...f, registration: e.target.value }))} />
                <small className="std-muted">
                  Punctuation is ignored for matching, so changing it to a plate another
                  vehicle already holds is refused rather than merging the two.
                </small>
              </div>
              <div className="detail-item">
                <label>Make</label>
                <input className="std-input" value={form.make}
                  onChange={(e) => setForm((f) => ({ ...f, make: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Model</label>
                <input className="std-input" value={form.model}
                  onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Year</label>
                <input className="std-input" type="number" value={form.modelYear}
                  onChange={(e) => setForm((f) => ({ ...f, modelYear: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Colour</label>
                <input className="std-input" value={form.colour}
                  onChange={(e) => setForm((f) => ({ ...f, colour: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Fuel</label>
                <select className="std-input" value={form.fuelType}
                  onChange={(e) => setForm((f) => ({ ...f, fuelType: e.target.value }))}>
                  <option value="">—</option>
                  {FUEL_TYPES.map((f) => <option key={f} value={f}>{f.toUpperCase()}</option>)}
                </select>
              </div>
              <div className="detail-item">
                <label>Notes</label>
                <input className="std-input" value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="detail-item std-form-actions">
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save vehicle'}
                </button>
                <button className="btn btn-secondary btn-sm" disabled={saving}
                  onClick={() => { setEditing(false); fetchVehicle(); }}>Cancel</button>
              </div>
            </div>
          </>
        ) : (
          <div className="detail-grid">
            <div className="detail-item"><label>Registration</label><span>{vehicle.registration}</span></div>
            <div className="detail-item"><label>Matched as</label><span className="mono">{vehicle.registrationNumber}</span></div>
            <div className="detail-item"><label>Make &amp; model</label><span>{[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}</span></div>
            <div className="detail-item"><label>Year</label><span>{vehicle.modelYear || '—'}</span></div>
            <div className="detail-item"><label>Colour</label><span>{vehicle.colour || '—'}</span></div>
            <div className="detail-item"><label>Fuel</label><span>{vehicle.fuelType?.toUpperCase() || '—'}</span></div>
            {vehicle.notes && <div className="detail-item"><label>Notes</label><span>{vehicle.notes}</span></div>}
            <div className="detail-item"><label>On record since</label><span>{fmtDate(vehicle.createdAt)}</span></div>
          </div>
        )}
      </div>

      {/* ── Papers ── */}
      <div className="detail-section">
        <h2>📄 Documents</h2>
        <p className="std-muted">
          Each document carries its own expiry. A certificate on file with no date recorded
          is shown as such rather than counted as valid — that is how an expired paper hides
          in a green column.
        </p>
        <div className="std-images">
          {DOC_ORDER.map((kind) => {
            const d = vehicle.documents[kind];
            if (!d) return null;
            const style = STATE_STYLE[d.state] || STATE_STYLE.missing;
            return (
              <div key={kind} className="std-doc-block">
                <ImageField label={d.label} url={d.url} uploading={!!uploading[kind]}
                  onUpload={(f) => uploadDoc(kind, f)} size="sm" />
                <span className={`status-badge ${style.cls}`} style={{ fontSize: '0.7rem', marginTop: 4 }}>
                  {style.text(d)}
                </span>
                <input
                  type="date"
                  className="std-input"
                  style={{ marginTop: 4, fontSize: '0.75rem' }}
                  value={forInput(d.expiresOn)}
                  title={`${d.label} expiry`}
                  onChange={(e) => saveExpiry(kind, e.target.value)}
                />
              </div>
            );
          })}
          {/* Not a document that lapses — the plate photo, moved off the device
              by V250 so it travels with the car and not the tablet. */}
          <div className="std-doc-block">
            <ImageField label="Number plate" url={vehicle.documents.number_plate?.url}
              uploading={!!uploading.number_plate}
              onUpload={(f) => uploadDoc('number_plate', f)} size="sm" />
          </div>
        </div>
      </div>

      {/* ── Screens currently in this car ── */}
      <div className="detail-section">
        <h2>📺 Screens fitted</h2>
        {fitted.length === 0 ? (
          <div className="empty-state-small">
            <p>No screen is fitted in this vehicle.</p>
            <p className="muted">Fit one from that device&rsquo;s page.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {fitted.map((f) => (
              <div key={f.id} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/devices/${f.deviceId}`} className="tenant-name">📺 {f.deviceName}</Link>
                  {f.city && <span className="tenant-plan" style={{ marginLeft: '0.5rem' }}>{f.city}</span>}
                </div>
                <div className="tenant-role">
                  <span className="std-muted">{f.tenantName || 'Unassigned tenant'}</span>
                  {f.driverName && (
                    <> · {f.driverId
                      ? <Link to={`/drivers/${f.driverId}`}>🧑‍✈️ {f.driverName}</Link>
                      : <span>🧑‍✈️ {f.driverName}</span>}</>
                  )}
                </div>
                <span className="std-muted">since {fmtDate(f.assignedAt)} · {span(f.assignedAt, null)}</span>
              </div>
            ))}
          </div>
        )}
        {fitted.length > 1 && (
          <p className="std-hint">
            🔗 This car carries {fitted.length} screens. Legal — a front and a rear unit is a
            real arrangement — but worth confirming it is not a missed removal.
          </p>
        )}
      </div>

      {/* ── History ── */}
      {past.length > 0 && (
        <div className="detail-section">
          <h2>Previous screens</h2>
          <table className="std-history">
            <thead>
              <tr><th>Device</th><th>Operator</th><th>From</th><th>To</th><th>Duration</th></tr>
            </thead>
            <tbody>
              {past.map((f) => (
                <tr key={f.id}>
                  <td><Link to={`/devices/${f.deviceId}`}>{f.deviceName}</Link></td>
                  <td>{f.tenantName || '—'}</td>
                  <td>{fmtDate(f.assignedAt)}</td>
                  <td>{fmtDate(f.unassignedAt)}</td>
                  <td>{span(f.assignedAt, f.unassignedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VehicleDetail;
